/**
 * IPC kanal adlarının tek kaynağı. Main ve preload aynı sabiti kullanır —
 * kanal adı bir tarafta değişip diğerinde unutulduğunda derleme hatası alınır,
 * çalışma anında sessizce kaybolan bir çağrı olmaz.
 */
export const CHANNELS = {
  // Kayıt
  desktopSources: 'desktop-sources',
  saveRecording: 'save-recording',
  revealRecording: 'reveal-recording',
  // Ses dosyası yükleme (ana ekran)
  audioImportPick: 'audio-import-pick',
  audioImportTranscribe: 'audio-import-transcribe',
  audioImportDiscard: 'audio-import-discard',

  // Dışarıdan içe aktarma (Ayarlar > İçe aktar)
  importGranola: 'import-granola',
  importProgress: 'import-progress',

  // Canlı transkript (Deepgram)
  liveOpen: 'live-open',
  liveAudio: 'live-audio',
  liveClose: 'live-close',
  liveEvent: 'live-event',

  // Yapay zekâ
  aiChat: 'ai-chat',
  aiChatSend: 'ai-chat-send',
  chatThreadsList: 'chat-threads-list',
  chatThreadGet: 'chat-thread-get',
  chatThreadRename: 'chat-thread-rename',
  chatThreadDelete: 'chat-thread-delete',
  chatThreadsDeleteAll: 'chat-threads-delete-all',
  summarizeMeeting: 'summarize-meeting',

  // Kimlik doğrulama
  authSignIn: 'auth-sign-in',
  authSignOut: 'auth-sign-out',
  accountDelete: 'account-delete',
  authState: 'auth-state',

  // İlk kurulum ve yasal onaylar
  onboardingState: 'onboarding-state',
  onboardingAccept: 'onboarding-accept',
  onboardingComplete: 'onboarding-complete',
  legalLinks: 'legal-links',
  openMicPrivacy: 'open-mic-privacy',
  consentRequired: 'consent-required',
  updateRequired: 'update-required',

  // Profil ve arayüz dili (Ayarlar > Genel)
  profileGet: 'profile-get',
  profileUpdate: 'profile-update',
  profilePassword: 'profile-password',
  planUsage: 'plan-usage',
  appLanguageGet: 'app-language-get',
  appLanguageSet: 'app-language-set',

  // Kişisel tercihler (özet dili, tema, davranış) ve genel kısayollar
  appPrefsGet: 'app-prefs-get',
  appPrefsSave: 'app-prefs-save',
  shortcutAction: 'shortcut-action',
  recordingWindowMode: 'recording-window-mode',
  // Yüzen uyarı penceresi (takvim hatırlatıcısı / görüşme algılama)
  meetingPromptGet: 'meeting-prompt-get',
  meetingPromptShow: 'meeting-prompt-show',
  meetingPromptAction: 'meeting-prompt-action',
  meetingPromptRecord: 'meeting-prompt-record',
  callState: 'call-state',

  // Kalıcı depolama (Supabase)
  meetingsList: 'meetings-list',
  meetingCreate: 'meeting-create',
  meetingUpdate: 'meeting-update',
  meetingsDelete: 'meetings-delete',
  trashList: 'trash-list',
  trashRestore: 'trash-restore',
  trashPurge: 'trash-purge',
  meetingMediaGet: 'meeting-media-get',
  supportAccessGet: 'support-access-get',
  supportAccessSet: 'support-access-set',
  meetingsChanged: 'meetings-changed',

  // Kişiler ve şirketler
  peopleList: 'people-list',
  personProfile: 'person-profile',
  personUpdate: 'person-update',
  personDelete: 'person-delete',
  peopleMerge: 'people-merge',
  companiesList: 'companies-list',
  companyProfile: 'company-profile',
  companyCreate: 'company-create',
  companyUpdate: 'company-update',
  speakerAssign: 'speaker-assign',

  // Entegrasyon durumu ve arşiv dışa aktarımı
  exportArchive: 'export-archive',
  exportProgress: 'export-progress',
  revealFolder: 'reveal-folder',

  // Abonelik (iyzico, web API üzerinden)
  billingStatus: 'billing-status',
  billingCheckout: 'billing-checkout',
  billingCancel: 'billing-cancel',
  billingRefresh: 'billing-refresh',
  couponRedeem: 'coupon-redeem',

  // Toplantı botu (Skribby)
  botSend: 'bot-send',
  botStop: 'bot-stop',
  botScheduleMap: 'bot-schedule-map',
  autoJoinGet: 'auto-join-get',
  autoJoinSave: 'auto-join-save',
  autoJoinEvent: 'auto-join-event',

  // Takvim / Google
  calendarConnect: 'calendar-connect',
  calendarDisconnect: 'calendar-disconnect',
  calendarStatus: 'calendar-status',
  calendarUpcomingEvents: 'calendar-upcoming-events',
  calendarTodayEvents: 'calendar-today-events',
  calendarListGoogle: 'calendar-list-google',
  // Yardım al
  supportContact: 'support-contact',
  featureRequestSubmit: 'feature-request-submit',
  aiReportSubmit: 'ai-report-submit',
  calendarSetGoogleSelection: 'calendar-set-google-selection',
  outlookConnect: 'outlook-connect',
  outlookDisconnect: 'outlook-disconnect',
  outlookStatus: 'outlook-status',

  // Ayarlar (yalnızca yönetici)
  recordingPrefsGet: 'recording-prefs-get',
  settingsGet: 'settings-get',
  settingsSave: 'settings-save',
  diagnosticsGet: 'diagnostics-get',

  // Otomatik güncelleme
  updateStatusGet: 'update-status-get',
  updateCheck: 'update-check',
  updateInstall: 'update-install',
  updateStatus: 'update-status',

  // Özet e-postası
  summaryEmailStatus: 'summary-email-status',
  summaryEmailSave: 'summary-email-save',
  summaryEmailSend: 'summary-email-send',

  // Kabuk
  openExternal: 'open-external',

  // Geri bildirim
  saveFeedback: 'save-feedback',

  // Pencere
  windowMinimize: 'window-minimize',
  windowToggleMaximize: 'window-toggle-maximize',
  windowClose: 'window-close',
  windowIsMaximized: 'window-is-maximized',
  windowMaximizedChange: 'window-maximized-change',
} as const;

export type ChannelName = (typeof CHANNELS)[keyof typeof CHANNELS];
